<template lang="pug">
form.w-full.flex.flex-col.gap-8
  .bg-paper.surface.corners.p-8.flex.flex-col.gap-4.fade-in(
    :class="inputType === 'dropdown' ? 'mb-24' : ''"
  )
    uc-editable.font-bold(
      :editPath="`${editPath}.builder-question`"
      :k="`${screen.id}.builder-question`"
      :text="screen['builder-question']"
      uc-word-bold=""
    )
    template(v-if="!audioDoc")
      uc-preload-sfx(name="check")
      uc-preload-sfx(name="uncheck")
      .text-lg(v-if="inputType === 'dropdown'") 
        Listbox(v-model="selectedValues" v-slot="{ open }")
          ListboxButton.ripple.ripple_bg-gray-100.relative.max-w-prose.max-w-fit.border.border-gray-300.corners.bg-white.py-2.pl-4.pr-10.text-left.shadow-sm.focus-visible_ring.accent-primary-500.text-center.mx-auto(
            :class="submitted ? 'bg-gray-300 border-gray-400' : ''"
            :disabled="submitted"
            data-sfx="check"
          ) 
            span.block.truncate(
              :class="{ 'text-gray-500': submitted, italic: selectedOption?.name === 'disabled' }"
            ) 
              uc-text(
                :k="`${selectedOption.id}.${selectedOption.name}`"
                :text="selectedOption.label"
              )

              //- span {{ selectedOption }}
            uc-icon.opacity-50.absolute.right-3.top-3(icon="mdi:chevron-down")
          transition-expand
            ListboxOptions.absolute.mt-1.max-h-60.w-full.max-w-prose.max-w-fit.auto-alpha-0.overflow-auto.rounded-md.bg-white.py-1.text-base.shadow-lg.ring-1.ring-black.ring-opacity-5.transition.scale-95.simple-scrollbar.z-10(
              :class="{ 'auto-alpha-1 scale-100': open, 'left-0': smallDevice }"
              :static="true"
            )
              ListboxOption(
                :disabled="name === 'disabled'"
                :key="name"
                :uc-word-skip="name === 'disabled' || null"
                :value="name === 'disabled' ? false : label"
                v-for="{ name, label, ...option } in options"
                v-slot="{ active, selected }"
              ) 
                .relative.cursor-default.select-none.py-2.pl-4.pr-10.transition-colors(
                  :class="[active ? 'bg-primary-100 text-primary-500' : 'text-gray-900', { 'opacity-30 italic': name === 'disabled' }]"
                )
                  span.prose.block.truncate(
                    :class="selected ? 'font-bold text-primary-500' : 'font-normal'"
                    :text="label"
                  ) 
                    uc-text(:k="`${option.id}.${name}`" :text="label")
                  span
                    uc-icon.absolute.right-3.top-3.text-primary-500(
                      :icon="`theme:check`"
                      v-if="selected"
                    )
      fieldset(
        v-else=""
      )
        .flex.flex-col.gap-3.grid(
          :class="screen['cols'] && mediumDevice ? `grid-cols-${screen['cols']}` : ''"
          )
          div(v-for="{ name, label, ...option } in options")
            label.flex.gap-3.items-start
              input.focus-visible_ring.accent-primary-500.my-2(
                :data-sfx="inputType === 'radio' ? 'check' : isSelected(label) ? 'uncheck' : 'check'"
                :disabled="name === 'disabled' || submitted"
                :name="screen.id"
                :type="inputType"
                :value="label"
                v-model="selectedValues"
              )
              | #{ '  ' }
              //-  {{ name }} {{ label }}
              uc-text.prose-stack.bold-after-checked(
                :k="`${option.id}.${name}`"
                :text="label"
                type="span"
              )
  uc-preload-sfx(name="builder-submit")
  .w-full.group.flex.justify-end.items-center(
    :class="smallDevice && 'flex-col gap-2'"
  )
    uc-button.btn.btn-primary.unlock.ripple(
      :class="smallDevice && 'w-full'"
      :disabled="disableSubmit || submitted"
      @click="onSubmit"
      data-sfx="builder-submit"
      type="button"
    )
      uc-text(:key="1" text="Submit")
</template>

<script lang="ts" setup>
import { computed, ref, watchEffect, watch, inject } from 'vue';
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core';
import { set, setCompleted, setTags } from '../lib/SCORM';
import useSuspend from '../lib/useSuspend';
import {
  Listbox,
  ListboxButton,
  ListboxOptions,
  ListboxOption
} from '@headlessui/vue';

interface Option {
  name: string;
  label: string;
  id?: string;
}

const suspend = useSuspend();
const audioDoc = inject('audioDoc') as boolean;
const breakpoints = useBreakpoints(breakpointsTailwind);
const smallDevice = breakpoints.smallerOrEqual('sm');
const mediumDevice = breakpoints.greaterOrEqual('md');

const props = defineProps<{
  editPath: string;
  screen: UcScreenRequiredId & {
    'builder-question'?: string;
    'builder-checkboxes'?: Option[];
    'builder-radios'?: Option[];
    'builder-dropdown'?: Option[];
  };
}>();

const inputType = computed(() =>
  props.screen['builder-radios']
    ? 'radio'
    : props.screen['builder-dropdown']
      ? 'dropdown'
      : 'checkbox'
);

/**
 * Trims both strings and arrays of strings
 * @param v
 */
const trim = (v: string[] | string) =>
  typeof v === 'string' ? v.trim() : v.map(v => v.trim());

const persistedValue = suspend.inputs?.[props.screen.id];

const selectedValues = ref<string[] | string>(
  persistedValue
    ? JSON.parse(persistedValue)
    : inputType.value === 'checkbox'
      ? []
      : null
);

const options = computed(() => {
  const list =
    props.screen['builder-checkboxes'] ||
    props.screen['builder-radios'] ||
    props.screen['builder-dropdown'];

  if (!list) return [];

  return list.map<Option>(o => {
    const { id, ..._o } = o;
    const name = Object.keys(_o)[0];
    const label = o[name].trim();

    return { name, label, id };
  });
});

const selectedOption = computed(
  () =>
    options.value.find(o => o.label === selectedValues.value) ||
    options.value[0]
);

const nameToLabel = {};

options.value.forEach(option => {
  nameToLabel[option.name] = option.label.trim();

  // automatically add disabled checkboxes to selectedValues if nothing is already stored
  if (
    inputType.value === 'checkbox' &&
    option.name === 'disabled' &&
    !persistedValue
  ) {
    selectedValues.value = [option.label];
  }
});

// force the user to make a choice when it's radios
const disableSubmit = ref(
  inputType.value === 'radio' || inputType.value === 'dropdown'
);

const submitted = computed(() => {
  const sectionId = inject('sectionId') as string;

  return sectionId && suspend.completed[sectionId];
});

function isSelected(label: string) {
  if (!selectedValues.value) return false;
  return selectedValues.value.includes(label);
}

const onSubmit = () => {
  const sectionId = inject('sectionId') as string;
  setCompleted(sectionId);
};

watchEffect(() => {
  if (inputType.value === 'dropdown' && !selectedValues.value) {
    selectedValues.value = options.value[0].label;
  }
});

watch(selectedValues, () => {
  set(`inputs.${props.screen.id}`, JSON.stringify(trim(selectedValues.value)));
});

watch(selectedValues, v => {
  const tags: Tags = {};
  const foundTags = new Set<string>();

  // loop twice, in case there are two labels with the same input have different values
  options.value.forEach(o => {
    const found = typeof v === 'string' ? o.label === v : v.includes(o.label);
    if (found) foundTags.add(o.name);
  });

  options.value.forEach(o => {
    tags[o.name] = foundTags.has(o.name);
  });

  setTags(tags);
  disableSubmit.value = false;
});
</script>

<style>
input:checked ~ .bold-after-checked {
  @apply font-bold;
}
</style>

<style scoped>
form {
  --auto-alpha-dur-0: 50ms;
  --auto-alpha-dur-1: 100ms;
}
</style>
